import { useEffect, useState } from 'react';
import { Navigate, Outlet, useSearchParams } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import { useServerFlag } from '../hooks/useServerFlag';
import { hasStoredSession } from '../lib/storedSession';
import { isNativeApp } from '../lib/platform';
import { SIGN_IN_LANDING_PATH } from './Landing/v2/landingContent';

/** The longest the hold stays blank. Past it the page's own loading UI shows, as before B418 (an outage). */
export const SIGNED_OUT_HOLD_CAP_MS = 4000;

/**
 * B430 - the 4 s is the signed-out visitor's TOTAL blank, from the hold's start through the landing's own
 * fallback (founder 2026-10-21, ruling 7). The hold writes when it started holding; the landing's lazy-route
 * fallback (App.tsx) asks how much of the 4 s is left. It is a start time, not a flag: it runs out by itself
 * (a later visit, or one past the cap, finds 0 left) and nothing has to clear it for a direct /landing visit to
 * show its text at once. A hold that releases because a session arrived clears it, so a signed-in user never
 * inherits a blank.
 */
let blankStartedAt: number | null = null;

/** How much of the hold's 4 s a landing fallback that mounts now may still keep blank (0 when no hold ran, or it has run out). */
export function signedOutBlankRemainingMs(now: number = Date.now()): number {
  if (blankStartedAt === null) return 0;
  return Math.min(SIGNED_OUT_HOLD_CAP_MS, Math.max(0, SIGNED_OUT_HOLD_CAP_MS - (now - blankStartedAt)));
}

/** Forget the hold's start (a session arrived; also the reset point for tests). */
export function endSignedOutBlank(): void {
  blankStartedAt = null;
}

/**
 * B418 - a layout route for the guarded pages (the ones that send a signed-out visitor to the
 * landing). On the web, a visitor with NO saved session who opens one sees nothing until the
 * sign-in status is known, then the landing; the page's own loading UI (Today's skeleton, "Loading
 * season plan.") never paints for someone it is about to redirect.
 *
 * Whether to hold is decided ONCE, when the hold mounts, from `hasStoredSession()`. A device with
 * a saved session (or a storage that cannot be read) is never held: it renders the page exactly
 * as before, its own loading UI and Today's layout memory included. The native shell is never
 * held either (its boot has its own veil). The hold ends on `useAuthContext().loading`, the
 * provider's own bounded handshake, so a signed-out visitor waits no longer than the provider
 * does.
 *
 * B418 fix round 1 (founder 2026-10-21, "all as recommended"):
 *   - When a held visitor settles signed out (no session, `loading` false) with `landing_2_0_v1`
 *     on, the hold itself redirects to `SIGN_IN_LANDING_PATH` and the guarded page never mounts,
 *     so its lazy chunk is never fetched and its Suspense text ("Loading settings...") never
 *     paints. Flag off, a saved session and the native shell are unchanged: the page mounts and
 *     does what it did before (including /races' flag-off sign-in line).
 *   - While held, `prefetch` starts the landing chunk (App.tsx passes the same loader the lazy
 *     landing route uses), so the landing paints without its own loading text.
 *   - The blank is capped at `SIGNED_OUT_HOLD_CAP_MS`: a provider still loading then (an outage:
 *     the first config cycle can run about 38 s) releases to the page's own loading UI.
 *
 * `publicParam` names a query parameter that makes the page one a signed-out visitor may see
 * (Today's `?user=newbie` design preview needs no session): with it present at mount, no hold.
 *
 * It adds no element: held, it renders null; released, it renders the matched child route as the
 * layout `<Outlet />` and nothing else.
 */
export function SignedOutHold({
  publicParam,
  prefetch,
}: {
  publicParam?: string;
  prefetch?: () => Promise<unknown>;
}): JSX.Element | null {
  const { loading, session } = useAuthContext();
  // Read above every early return, with the id as a literal (the config export rake reads literal call sites).
  const landing2On = useServerFlag('landing_2_0_v1');
  const [params] = useSearchParams();
  const [holds] = useState(
    () =>
      !isNativeApp() &&
      !(publicParam !== undefined && params.get(publicParam) !== null) &&
      !hasStoredSession(),
  );
  const [capped, setCapped] = useState(false);

  // B430: the start of the visitor's blank, for the landing fallback to share. A session arriving ends it.
  useEffect(() => {
    if (!holds) return;
    blankStartedAt = Date.now();
  }, [holds]);
  const signedIn = Boolean(session);
  useEffect(() => {
    if (holds && !loading && signedIn) endSignedOutBlank();
  }, [holds, loading, signedIn]);

  useEffect(() => {
    if (!holds || prefetch === undefined) return;
    Promise.resolve()
      .then(prefetch)
      .catch(() => {});
  }, [holds, prefetch]);

  useEffect(() => {
    if (!holds || !loading || capped) return undefined;
    const timer = setTimeout(() => setCapped(true), SIGNED_OUT_HOLD_CAP_MS);
    return () => clearTimeout(timer);
  }, [holds, loading, capped]);

  if (holds && loading && !capped) return null;
  if (holds && !loading && !session && landing2On) {
    return <Navigate to={SIGN_IN_LANDING_PATH} replace />;
  }
  return <Outlet />;
}
